<template>
    <base-dialog :loading="loading" :value="dialog" :title="$t('common.select_coach')" :width="440" @input="onInput" @close="hide">
        <kimsa-text-field class="mb-3" @change="search = $event" :value="search" :placeholder="$t('common.search')"
            prepend-inner-icon="mdi-magnify" dense />

        <skeleton-loader v-if="loading" type="chat-list" :count="4" />

        <div v-else-if="filteredCoaches.length" class="coach-list">
            <button v-for="coach in filteredCoaches" :key="coach.id" type="button" class="coach-row"
                :class="{ 'coach-row--active': coach.id === selectedId }" @click="select(coach)">
                <student-avatar :avatar="coach.avatar" :name="coach.name" :avatar-key="coach.id" :size="34"
                    :radius="8" />
                <div class="coach-row__text">
                    <div class="coach-row__name">{{ coach.name }}</div>
                    <div class="coach-row__email">{{ coach.email }}</div>
                </div>
                <v-icon v-if="coach.id === selectedId" size="18" color="primary">mdi-check-circle</v-icon>
            </button>
        </div>

        <div v-else class="coach-empty">{{ $t('common.not_found') }}</div>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import BaseDialog from "@/components/common/BaseDialog";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import StudentAvatar from "@/components/common/StudentAvatar";

export default {
    name: "SetCoachDialog",
    components: { BaseDialog, KimsaTextField, SkeletonLoader, StudentAvatar },
    data() {
        return {
            dialog: false,
            loading: false,
            search: '',
            coaches: [],
        }
    },
    computed: {
        ...mapState(['libraryCoach']),
        selectedId(vm = this) {
            return vm.libraryCoach?.id || null
        },
        filteredCoaches(vm = this) {
            if (!vm.search) return vm.coaches
            const query = vm.search.toLowerCase()
            return vm.coaches.filter(coach => `${coach.name} ${coach.email}`.toLowerCase().includes(query))
        },
    },
    methods: {
        ...mapActions(['setLibraryCoach', 'fetchCompanyCoaches']),
        show() {
            const vm = this
            vm.dialog = true
            vm.loadCoaches()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.search = ''
        },
        onInput(val) {
            if (!val) this.hide()
        },
        async loadCoaches() {
            const vm = this
            if (vm.coaches.length) return
            vm.loading = true
            const hits = await vm.fetchCompanyCoaches(vm.currentUser?.uid)
            vm.coaches = hits.map(hit => ({
                id: hit.objectID,
                name: hit.name || hit.email || vm._noAvailableName,
                email: hit.email || '',
                avatar: hit.avatar || null,
            }))
            vm.loading = false
        },
        async select(coach) {
            const vm = this
            vm.hide()
            if (coach.id === vm.selectedId) return
            await vm.setLibraryCoach(coach)
        },
    },
}
</script>

<style scoped>
.coach-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.coach-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: 10px;
    background: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s, border-color 0.15s;
}

.coach-row:hover {
    background: var(--v-surface2-base);
}

.coach-row--active {
    border-color: var(--v-primary-base);
    background: var(--v-surface2-base);
}

.theme--light .coach-row--active {
    border-color: var(--v-accent-base);
}

.coach-row__text {
    flex: 1;
    min-width: 0;
}

.coach-row__name {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coach-row__email {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coach-empty {
    padding: 28px 0;
    text-align: center;
    font-size: var(--fs-body);
    color: var(--v-txtMuted-base);
}
</style>
